import type { SpacingScale } from '@meterup/atto';
import type { ColumnFiltersState } from '@tanstack/table-core';
import type { ReactNode } from 'react';
import {
  Button,
  colors,
  ControlGroup,
  darkThemeSelector,
  Divider,
  DropdownMenu,
  DropdownMenuButton,
  DropdownMenuCheckboxItem,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuPopover,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  HorizontalScrollShadow,
  HStack,
  Icon,
  sizing,
  styled,
  Text,
  useHorizontalShadowOnScroll,
} from '@meterup/atto';
import { useMeasure } from '@uidotdev/usehooks';
import { isNumber } from 'lodash-es';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type {
  FilterComponentFunction,
  FilterComponentProps,
  FilterOption,
  FilterOptions,
  FilterValue,
  FilterVariant,
  NormalizedFilterOption,
  SharedColumnDef,
} from '../createColumnBuilder';
import type { AutoCollectionStateInterface, Filter, Table } from '../utils';
import type { TableFiltersActions } from './TableFiltersActions';
import { sortingCollator } from '../../../helpers/utils/strings';
import { parseDateTime } from '../../../topics/time';
import { DateTimeRangeStorageProvider } from '../../DateTimeRangePicker/DateTimeRangeStorageContext';
import { FormatDateTime } from '../../FormatDateTime';
import { KeyBoundSearchInput } from '../../KeyBoundSearchInput';
import { TableColumnsEnhanced } from '../TableColumns/TableColumns';
import { TablePresets } from '../TablePresets/TablePresets';
import { TableFilterVariant } from '../utils';
import { TableFiltersActionsMenu } from './TableFiltersActions';
import {
  TableFiltersFieldDateRange,
  TableFiltersFieldMultiSelect,
  TableFiltersFieldNumber,
  TableFiltersFieldNumberRange,
  TableFiltersFieldSelect,
  TableFiltersFieldText,
} from './TableFiltersField/TableFiltersField';
import { ServerTableFiltersToken, TableFiltersToken } from './TableFiltersToken';
import { isFilterApplied } from './utils';

const tableFiltersVars = {
  padding: {
    sides: 8,
    ends: 6,
  },
} satisfies {
  padding: {
    sides: SpacingScale;
    ends: SpacingScale;
  };
};

type SelectedFiltersDropdownOptions = {
  value: string;
  label: string;
  group?: string | null;
};

type SelectedFiltersDropdownProps = {
  options: Array<SelectedFiltersDropdownOptions>;
  selectedFilters: string[];
  setSelectedFilters: (filters: string[]) => void;
  groupOrder?: Map<string, number>;
  loading?: boolean;
};

const DEFAULT_FILTER_GROUP_NAME = 'Filters';

function DisplayForFilterValue({
  value,
  options,
}: {
  value: FilterValue;
  options?: FilterOption[];
}): ReactNode {
  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No';
  }

  if (!value) {
    return 'All';
  }

  if (Array.isArray(value)) {
    if (value.length === 0) {
      return 'All';
    }

    if (options) {
      return (
        <HStack spacing={4}>
          {value.map((v, index) => (
            <HStack key={v} wrap="nowrap" align="center">
              {options.find((opt) => opt.value === v)?.label ?? v}
              {index < value.length - 1 ? <Text>,</Text> : null}
            </HStack>
          ))}
        </HStack>
      );
    }

    if (value[0] === null && value[1] === null) {
      return null;
    }
    if (isNumber(value[0]) || isNumber(value[1])) {
      return (
        <HStack align="center" spacing={4} wrap="nowrap">
          {value[0]}
          <Icon icon="arrow-right" size={10} />
          {value[1]}
        </HStack>
      );
    }

    const startDate = parseDateTime(value[0]);
    const endDate = parseDateTime(value[1]);
    if (startDate || endDate) {
      return (
        <HStack align="center" spacing={4} wrap="nowrap">
          <FormatDateTime value={startDate} />
          <Icon icon="arrow-right" size={10} />
          <FormatDateTime value={endDate} />
        </HStack>
      );
    }

    return value.join(', ');
  }

  if (options) {
    return options.find((opt) => opt.value === value)?.label;
  }

  return value;
}

function SelectedFiltersDropdown({
  options,
  selectedFilters,
  setSelectedFilters,
  groupOrder,
  loading,
}: SelectedFiltersDropdownProps) {
  const groupedOptions = useMemo(() => {
    const groups = new Map<string, Array<{ value: string; label: string }>>();

    for (const opt of options) {
      const groupName = opt.group ?? DEFAULT_FILTER_GROUP_NAME;
      let arr = groups.get(groupName);
      if (!arr) {
        arr = [];
        groups.set(groupName, arr);
      }
      arr.push({ value: opt.value, label: opt.label });
    }

    return Array.from(groups.entries()).sort(([groupA], [groupB]) => {
      if (groupA === DEFAULT_FILTER_GROUP_NAME && groupB !== DEFAULT_FILTER_GROUP_NAME) return -1;
      if (groupA !== DEFAULT_FILTER_GROUP_NAME && groupB === DEFAULT_FILTER_GROUP_NAME) return 1;

      // If groupOrder is provided, sort by column order; otherwise fall back to alphabetical
      if (groupOrder) {
        const orderA = groupOrder.get(groupA) ?? Infinity;
        const orderB = groupOrder.get(groupB) ?? Infinity;
        if (orderA !== orderB) {
          return orderA - orderB;
        }
      }

      return groupA.localeCompare(groupB);
    });
  }, [options, groupOrder]);

  return (
    <DropdownMenu>
      <DropdownMenuButton
        tabIndex={0}
        appearance="muted"
        arrangement="hidden-label"
        icon="filter"
        size="small"
        loading={loading}
      >
        Add a filter...
      </DropdownMenuButton>
      <DropdownMenuPopover align="start">
        {groupedOptions.map(([groupName, groupOptions]) => (
          <DropdownMenuGroup key={groupName} label={groupName}>
            {groupOptions.map((opt) => (
              <DropdownMenuCheckboxItem
                key={opt.value}
                checked={selectedFilters.includes(opt.value)}
                closeOnClick={false}
                onCheckedChange={(checked) => {
                  const newVal = checked
                    ? [...selectedFilters, opt.value]
                    : selectedFilters.filter((selected) => selected !== opt.value);
                  setSelectedFilters(newVal);
                }}
              >
                {opt.label}
              </DropdownMenuCheckboxItem>
            ))}
          </DropdownMenuGroup>
        ))}
      </DropdownMenuPopover>
    </DropdownMenu>
  );
}

const TableFiltersDivider = styled(Divider, {
  padding: '$4 $2',
});
TableFiltersDivider.displayName = 'TableFiltersDivider';

const TableFiltersViews = styled(ControlGroup, {
  gridArea: 'views',
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  gap: '$8',
  padding: `$${tableFiltersVars.padding.ends} $${tableFiltersVars.padding.sides / 2} $${tableFiltersVars.padding.ends} ${sizing.sides}`,
});
TableFiltersViews.displayName = 'TableFiltersViews';

const TableFiltersOptionsScroll = styled(HorizontalScrollShadow, {
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  gap: '$8',
  maxWidth: '100%',
  padding: `$${tableFiltersVars.padding.ends} ${sizing.sides}`,

  variants: {
    hasControls: {
      true: {},
      false: {},
    },
    hasViews: {
      true: {},
      false: {},
    },
    isNarrow: {
      true: {},
      false: {},
    },
  },

  compoundVariants: [
    {
      hasControls: true,
      isNarrow: false,
      css: {
        paddingRight: `$${tableFiltersVars.padding.sides / 2}`,
      },
    },
    {
      hasViews: true,
      isNarrow: false,
      css: {
        paddingLeft: `$${tableFiltersVars.padding.sides / 2}`,
      },
    },
  ],
});
TableFiltersOptionsScroll.displayName = 'TableFiltersOptionsScroll';

const TableFiltersOptions = styled('div', {
  gridArea: 'options',
  position: 'relative',
  maxWidth: '100%',
  minWidth: 0,

  '&::before': {
    content: '',
    position: 'absolute',
    top: '-0.5px',
    right: `${sizing.sides}`,
    left: `${sizing.sides}`,
    height: '$1',
    background: colors.strokeNeutralLight,

    [darkThemeSelector]: {
      background: colors.strokeNeutralDark,
    },
  },
});
TableFiltersOptions.displayName = 'TableFiltersOptions';

const TableFiltersControls = styled(ControlGroup, {
  gridArea: 'controls',
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'flex-end',
  gap: '$8',
  padding: `$${tableFiltersVars.padding.ends} ${sizing.sides} $${tableFiltersVars.padding.ends} $${tableFiltersVars.padding.sides}`,
});
TableFiltersControls.displayName = 'TableFiltersControls';

const TableFiltersContainer = styled('div', {
  display: 'grid',
  width: '100%',

  variants: {
    isNarrow: {
      true: {
        gridTemplateColumns: 'min-content 1fr min-content',
        gridTemplateRows: "'min-content' 'min-content'",
        gridTemplateAreas: `
          "views blank controls"
          "options options options"
        `,
      },
      false: {
        gridTemplateColumns: 'min-content 1fr min-content',
        gridTemplateAreas: `
          "views options controls"
        `,
        width: '100%',
      },
    },
  },
});
TableFiltersContainer.displayName = 'TableFiltersContainer';

type FilterMapVal<RowType> = Omit<
  FilterComponentProps<RowType>,
  'handleFiltersChange' | 'onKeyDown'
> & {
  label: string;
  filter?: FilterComponentFunction<RowType>;
  variant?: FilterVariant;
  options?: FilterOptions<RowType>;
};

function TableFilter<RowType>({
  mappedFilter,
  options,
  handleFiltersChange,
  handleFilterValueSelected,
}: {
  mappedFilter: FilterMapVal<RowType>;
  options: FilterOption[] | undefined;
  handleFiltersChange: (id: string, value: FilterValue) => void;
  handleFilterValueSelected?: (id: string) => void;
}) {
  if (mappedFilter?.filter) {
    return mappedFilter.filter({
      ...mappedFilter,
      handleFiltersChange,
      onKeyDown: () => {},
    });
  }

  switch (mappedFilter.variant) {
    case 'select':
      return (
        <TableFiltersFieldSelect
          {...mappedFilter}
          options={options!}
          value={mappedFilter.value as string}
          handleFiltersChange={handleFiltersChange}
          onKeyDown={() => {}}
          onValueSelected={
            handleFilterValueSelected ? () => handleFilterValueSelected(mappedFilter.id) : undefined
          }
        />
      );
    case 'multi-select':
      return (
        <TableFiltersFieldMultiSelect
          {...mappedFilter}
          options={options!}
          value={mappedFilter.value as string[]}
          handleFiltersChange={handleFiltersChange}
          onKeyDown={() => {}}
        />
      );
    case 'number-range':
      return (
        <TableFiltersFieldNumberRange
          {...mappedFilter}
          value={mappedFilter.value as [number | null, number | null]}
          handleFiltersChange={handleFiltersChange}
          onKeyDown={() => {}}
          onValueSelected={
            handleFilterValueSelected ? () => handleFilterValueSelected(mappedFilter.id) : undefined
          }
        />
      );
    case 'number':
      return (
        <TableFiltersFieldNumber
          {...mappedFilter}
          value={mappedFilter.value as number | null}
          handleFiltersChange={handleFiltersChange}
          onKeyDown={() => {}}
          onValueSelected={
            handleFilterValueSelected ? () => handleFilterValueSelected(mappedFilter.id) : undefined
          }
        />
      );
    case 'text':
      return (
        <TableFiltersFieldText
          {...mappedFilter}
          value={mappedFilter.value as string}
          onChange={handleFiltersChange}
          onKeyDown={() => {}}
          onValueSelected={
            handleFilterValueSelected ? () => handleFilterValueSelected(mappedFilter.id) : undefined
          }
        />
      );
    case 'date-range':
      return (
        <DateTimeRangeStorageProvider>
          <TableFiltersFieldDateRange
            {...mappedFilter}
            value={mappedFilter.value as [string | null, string | null]}
            handleFiltersChange={handleFiltersChange}
            onKeyDown={() => {}}
            onValueSelected={
              handleFilterValueSelected
                ? () => handleFilterValueSelected(mappedFilter.id)
                : undefined
            }
          />
        </DateTimeRangeStorageProvider>
      );
    default:
      return null;
  }
}

export function TableFilters<RowType>({
  columns = [],
  data,
  filtersState = [],
  onFiltersChange = () => {},
  onClearFilters,
  controls,
  staticFilters,
}: {
  columns?: SharedColumnDef<RowType, any>[];
  data?: RowType[];
  filtersState?: ColumnFiltersState;
  onFiltersChange?: (columnState: ColumnFiltersState) => void;
  onClearFilters?: React.MouseEventHandler<HTMLButtonElement>;
  controls?: ReactNode;
  staticFilters?: ReactNode;
}) {
  const { getBoxShadow, onScrollHandler, handleTargetChange } = useHorizontalShadowOnScroll('both');
  const optionsRef = useRef<HTMLDivElement>(null);
  const [openFilters, setOpenFilters] = useState<Set<string>>(new Set());

  const handleResizeOptions = useCallback(() => {
    if (optionsRef.current) {
      handleTargetChange(optionsRef.current);
    }
  }, [handleTargetChange]);

  useEffect(() => {
    handleResizeOptions();
    window.addEventListener('resize', handleResizeOptions);
    return () => {
      window.removeEventListener('resize', handleResizeOptions);
    };
  }, [handleResizeOptions]);

  const filters: FilterMapVal<RowType>[] = useMemo(() => {
    const filterList: FilterMapVal<RowType>[] = [];

    for (const col of columns) {
      // Add main column filter if it has filter configuration
      if (col && (col.filterFn || col.filter) && col.filterVariant) {
        const label = (col.filterLabel ?? col.header) as string;
        const value = filtersState?.find((filter) => filter.id === col.id)?.value as FilterValue;

        filterList.push({
          id: col.id,
          value,
          label,
          data,
          filter: col.filter,
          options: col.filterOptions,
          variant: col.filterVariant,
        });
      }

      // Add subline filter if column has subline and sublineFilterVariant is defined
      if (
        col?.meta?.subline &&
        col.sublineFilterVariant &&
        (col.sublineFilterFn || col.sublineFilter)
      ) {
        const sublineId = `${col.id}-subline`;
        const baseLabel = col.filterLabel ?? col.header;
        const label = col.sublineFilterLabel ?? (baseLabel ? `${baseLabel} (subline)` : 'Subline');
        const value = filtersState?.find((filter) => filter.id === sublineId)?.value as FilterValue;

        filterList.push({
          id: sublineId,
          value,
          label,
          data,
          filter: col.sublineFilter,
          options: col.sublineFilterOptions,
          variant: col.sublineFilterVariant,
        });
      }
    }

    return filterList;
  }, [columns, filtersState, data]);

  const filtersMap = useMemo(() => {
    const map = new Map<string, FilterMapVal<RowType>>();
    for (const f of filters) {
      map.set(f.id, f);
    }
    return map;
  }, [filters]);

  const filterOptionsResult = useMemo(() => {
    const optionsMap = new Map<string, { value: string; label: string; group?: string | null }>();
    const groupOrderMap = new Map<string, number>();
    let columnIndex = 0;

    for (const col of columns) {
      // Add main column filter option
      if ((col?.filterFn || col?.filter) && col?.filterVariant && col.id) {
        const label = (col.filterLabel ?? col.header) as string;
        const group = col.meta?.group ?? null;
        const groupName = group ?? DEFAULT_FILTER_GROUP_NAME;

        // Track the first occurrence of each group
        if (!groupOrderMap.has(groupName)) {
          groupOrderMap.set(groupName, columnIndex);
        }

        optionsMap.set(col.id, {
          value: col.id,
          label,
          group,
        });
        columnIndex += 1;
      }

      // Add subline filter option if column has subline and sublineFilterVariant
      if (
        col?.meta?.subline &&
        col.sublineFilterVariant &&
        (col.sublineFilterFn || col.sublineFilter) &&
        col.id
      ) {
        const sublineId = `${col.id}-subline`;
        const baseLabel = col.filterLabel ?? col.header;
        const label = col.sublineFilterLabel ?? (baseLabel ? `${baseLabel} (subline)` : 'Subline');
        const group = col.meta?.group ?? null;
        const groupName = group ?? DEFAULT_FILTER_GROUP_NAME;

        // Track the first occurrence of each group (use the same index as parent column)
        if (!groupOrderMap.has(groupName)) {
          groupOrderMap.set(groupName, columnIndex - 1);
        }

        optionsMap.set(sublineId, {
          value: sublineId,
          label,
          group,
        });
      }
    }
    return { options: Array.from(optionsMap.values()), groupOrder: groupOrderMap };
  }, [columns]);

  const { options: filterOptions, groupOrder } = filterOptionsResult;

  const handleFiltersChange = useCallback(
    (id: string, value: FilterValue) => {
      let newFilters = [];
      const prevFilter = filtersState?.find((col) => col.id === id);
      if (prevFilter) {
        newFilters =
          filtersState?.map((col) => {
            if (col.id === id) {
              return {
                ...col,
                value,
              };
            }
            return { ...col };
          }) ?? [];
      } else {
        newFilters = [...(filtersState ?? []), { id, value }];
      }
      onFiltersChange(newFilters);
    },
    [filtersState, onFiltersChange],
  );

  const handleSelectedFiltersChange = useCallback(
    (ids: string[]) => {
      function initValForVariant(variant?: FilterVariant): unknown {
        if (variant) {
          switch (variant) {
            case 'select':
              return null;
            case 'multi-select':
              return [];
            case 'text':
              return '';
            case 'number-range':
            case 'date-range':
              return [null, null];
            case 'number':
              return null;
            case 'boolean':
              return true;
            default:
              return null;
          }
        }
        return null;
      }

      // Find newly added filters
      const currentFilterIds = new Set(filtersState?.map((filter) => filter.id) || []);
      const newlyAddedFilters = ids.filter((id) => !currentFilterIds.has(id));

      const newFilters = [];
      for (const id of ids) {
        const prevFilter = filtersState?.find((filter) => filter.id === id);
        if (prevFilter) {
          newFilters.push(prevFilter);
        } else {
          const newFilter = filtersMap.get(id);
          if (newFilter) {
            newFilters.push({
              id,
              value: initValForVariant(newFilter?.variant),
            });
          }
        }
      }

      // Automatically open newly added filters
      if (newlyAddedFilters.length > 0) {
        setOpenFilters((prev) => new Set([...prev, ...newlyAddedFilters]));
      }

      onFiltersChange(newFilters);
    },
    [filtersMap, filtersState, onFiltersChange],
  );

  const handleClearFilters = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      onFiltersChange([]);
      if (onClearFilters) {
        onClearFilters(e);
      }
    },
    [onFiltersChange, onClearFilters],
  );

  const handleClearFilter = useCallback(
    (id: string) => {
      const filteredIds =
        filtersState?.filter((filter) => filter.id !== id).map((filter) => filter.id) ?? [];
      handleSelectedFiltersChange(filteredIds);
    },
    [filtersState, handleSelectedFiltersChange],
  );

  const handleFilterOpenChange = useCallback((id: string, isOpen: boolean) => {
    setOpenFilters((prev) => {
      const newSet = new Set(prev);
      if (isOpen) {
        newSet.add(id);
      } else {
        newSet.delete(id);
      }
      return newSet;
    });
  }, []);

  const handleFilterValueSelected = useCallback((id: string) => {
    setOpenFilters((prev) => {
      const newSet = new Set(prev);
      newSet.delete(id);
      return newSet;
    });
  }, []);

  const hasViews = Boolean(staticFilters);
  const hasColumns = Boolean(columns?.length !== 0);
  const hasFilters = Boolean(hasColumns && filtersState?.length > 0);
  const hasControls = Boolean(controls || hasFilters);

  const [ref, { width }] = useMeasure();
  const isNarrow = Boolean(width && width < 700);

  return (
    <TableFiltersContainer ref={ref} isNarrow={isNarrow}>
      {hasViews && (
        <TableFiltersViews relation="separate" size="small">
          {staticFilters}
          {hasColumns && (
            <TableFiltersDivider appearance="thick" direction="column" variant="neutral" />
          )}
        </TableFiltersViews>
      )}
      {hasColumns && filters.length > 0 && (
        <TableFiltersOptions>
          <TableFiltersOptionsScroll
            isNarrow={isNarrow}
            hasViews={hasViews}
            hasControls={hasControls}
            ref={optionsRef}
            onScroll={onScrollHandler}
            {...getBoxShadow}
          >
            {filtersState.map((filter) => {
              const mappedFilter = filtersMap.get(filter.id);
              const options: FilterOption[] | undefined =
                mappedFilter && typeof mappedFilter.options === 'function'
                  ? mappedFilter.options(data)
                  : (mappedFilter?.options as FilterOption[] | undefined);

              if (mappedFilter) {
                const sharedTokenProps = {
                  id: mappedFilter.id,
                  key: mappedFilter.id,
                  handle: mappedFilter.label,
                  value: <DisplayForFilterValue value={mappedFilter.value} options={options} />,
                  onTokenClear: () => handleClearFilter(mappedFilter.id),
                  modified: isFilterApplied(mappedFilter.value),
                };

                if (mappedFilter.variant === 'boolean') {
                  return (
                    <TableFiltersToken
                      {...sharedTokenProps}
                      variant="toggle"
                      onToggleClick={() =>
                        handleFiltersChange(mappedFilter.id, !(mappedFilter.value as boolean))
                      }
                      toggleSelected={mappedFilter.value as boolean}
                    />
                  );
                }

                return (
                  <TableFiltersToken
                    {...sharedTokenProps}
                    changeable
                    isOpen={openFilters.has(mappedFilter.id)}
                    onOpenChange={(isOpen) => handleFilterOpenChange(mappedFilter.id, isOpen)}
                  >
                    <TableFilter
                      mappedFilter={mappedFilter}
                      options={options!}
                      handleFiltersChange={handleFiltersChange}
                      handleFilterValueSelected={handleFilterValueSelected}
                    />
                  </TableFiltersToken>
                );
              }
              return null;
            })}
            {filtersState.length < filterOptions.length && (
              <SelectedFiltersDropdown
                options={filterOptions}
                selectedFilters={filtersState.map((filter) => filter.id)}
                setSelectedFilters={handleSelectedFiltersChange}
                groupOrder={groupOrder}
              />
            )}
          </TableFiltersOptionsScroll>
        </TableFiltersOptions>
      )}
      {hasControls && (
        <TableFiltersControls size="small" relation="separate">
          {hasFilters && !isNarrow && (
            <Button
              onClick={handleClearFilters}
              arrangement="hidden-label"
              appearance="muted"
              ghost
              icon="trash-can"
              size="small"
              variant="destructive"
            >
              Clear filters
            </Button>
          )}
          {hasFilters && controls && !isNarrow && (
            <TableFiltersDivider appearance="thick" direction="column" variant="neutral" />
          )}
          {controls && controls}
        </TableFiltersControls>
      )}
    </TableFiltersContainer>
  );
}

// Enhanced interface for TableState integration
function TableSort<RowType, C extends SharedColumnDef<RowType, any>>({
  columns,
  tableState,
}: {
  columns: C[];
  tableState: AutoCollectionStateInterface<RowType, C['id']>;
}) {
  const sortableColumns = useMemo(
    () =>
      columns.filter(
        (col): col is C & { id: string; header: string } =>
          typeof col.id === 'string' &&
          typeof col.header === 'string' &&
          col.enableSorting !== false,
      ),
    [columns],
  );

  if (sortableColumns.length === 0) return null;

  const currentSort = tableState.sortingState[0];
  const activeColumn = sortableColumns.find((c) => c.id === currentSort?.id) ?? sortableColumns[0];
  const isDesc = currentSort?.desc ?? false;

  return (
    <DropdownMenu>
      <DropdownMenuButton appearance="muted" arrangement="hidden-label" icon="sort">
        Sort
      </DropdownMenuButton>
      <DropdownMenuPopover align="start">
        <DropdownMenuGroup label="Sort by">
          <DropdownMenuRadioGroup
            value={activeColumn.id}
            onValueChange={(newColumnId) => {
              tableState.setSortingState([{ id: newColumnId, desc: isDesc }]);
            }}
          >
            {sortableColumns.map((col) => (
              <DropdownMenuRadioItem key={col.id} value={col.id} label={col.header} />
            ))}
          </DropdownMenuRadioGroup>
        </DropdownMenuGroup>
        <DropdownMenuGroup label="Direction">
          <DropdownMenuRadioGroup
            value={isDesc ? 'desc' : 'asc'}
            onValueChange={(dir) => {
              tableState.setSortingState([{ id: activeColumn.id, desc: dir === 'desc' }]);
            }}
          >
            <DropdownMenuRadioItem value="asc" label="Ascending" icon="arrow-up" />
            <DropdownMenuRadioItem value="desc" label="Descending" icon="arrow-down" />
          </DropdownMenuRadioGroup>
        </DropdownMenuGroup>
      </DropdownMenuPopover>
    </DropdownMenu>
  );
}

interface TableFiltersEnhancedBaseProps<RowType, C extends SharedColumnDef<RowType, any>> {
  columns?: C[];
  tableState: AutoCollectionStateInterface<RowType, C['id']>;
  controls?: ReactNode;
  overflowActions?: TableFiltersActions[];
  multiSelectActions?: ReactNode;
  /** When true, shows a sort dropdown in the filter bar. Intended for headerless layouts like AutoBlocks. */
  enableSortMenu?: boolean;
  /** When true, shows a customization menu in the filter bar. Intended for headerless layouts like AutoBlocks. */
  enableCustomizationMenu?: boolean;
  /** When true, column reordering is disabled (no drag handles in Columns dropdown). */
  disableColumnOrder?: boolean;
}

interface ClientTableFiltersEnhancedProps<RowType, C extends SharedColumnDef<RowType, any>>
  extends TableFiltersEnhancedBaseProps<RowType, C> {
  data?: RowType[];
  staticFilters?: ReactNode;
  hideGlobalFilter?: boolean;
  disableCSVExport?: boolean;
  /** Shortcut priority for search (0 = ⌘F, 1 = ⌘G). Lower = primary. */
  shortcutPriority?: number;
}

interface ServerTableFiltersEnhancedProps<RowType, C extends SharedColumnDef<RowType, any>>
  extends TableFiltersEnhancedBaseProps<RowType, C> {
  table: Table<RowType>;
}

type TableFiltersEnhancedProps<RowType, C extends SharedColumnDef<RowType, any>> =
  | (ClientTableFiltersEnhancedProps<RowType, C> & { serverSide?: false })
  | (ServerTableFiltersEnhancedProps<RowType, C> & { serverSide: true });

function normalizeFilterOption(option: FilterOption | string): NormalizedFilterOption {
  if (typeof option === 'string') {
    return {
      value: option,
      label: option,
    };
  }

  if (!option.label) {
    return {
      ...option,
      label: option.value,
    };
  }

  return option as NormalizedFilterOption;
}

function normalizeFilterOptions<RowType>(
  data: RowType[] | undefined,
  filterOptions: FilterOptions<RowType> | undefined,
): NormalizedFilterOption[] | undefined {
  if (!filterOptions) {
    return undefined;
  }

  if (Array.isArray(filterOptions)) {
    return filterOptions.map(normalizeFilterOption);
  }

  const options = filterOptions(data)?.map(normalizeFilterOption) ?? [];
  const map = new Map<string, NormalizedFilterOption>();
  for (const option of options) {
    if (!map.has(option.value)) {
      map.set(option.value, option);
    }
  }

  return Array.from(map.values()).sort((a, b) => {
    const aKey = typeof a.label === 'string' ? a.label : a.value;
    const bKey = typeof b.label === 'string' ? b.label : b.value;
    return sortingCollator.compare(aKey, bKey);
  });
}

function ClientTableFiltersEnhanced<RowType, C extends SharedColumnDef<RowType, any>>({
  columns = [],
  data,
  tableState,
  controls,
  staticFilters,
  multiSelectActions,
  hideGlobalFilter,
  disableColumnOrder = false,
  enableCustomizationMenu = true,
  enableSortMenu = false,
  shortcutPriority,
  disableCSVExport = false,
  overflowActions,
}: ClientTableFiltersEnhancedProps<RowType, C>) {
  const { getBoxShadow, onScrollHandler, handleTargetChange } = useHorizontalShadowOnScroll('both');
  const optionsRef = useRef<HTMLDivElement>(null);
  const [openFilters, setOpenFilters] = useState<Set<string>>(new Set());

  const handleResizeOptions = useCallback(() => {
    if (optionsRef.current) {
      handleTargetChange(optionsRef.current);
    }
  }, [handleTargetChange]);

  useEffect(() => {
    handleResizeOptions();
    window.addEventListener('resize', handleResizeOptions);
    return () => {
      window.removeEventListener('resize', handleResizeOptions);
    };
  }, [handleResizeOptions]);

  const resolvedData = data ?? tableState.tableRef?.current?.data;

  const filters: FilterMapVal<RowType>[] = useMemo(() => {
    const filterList: FilterMapVal<RowType>[] = [];

    for (const col of columns) {
      // Add main column filter if it has filter configuration
      if (col && (col.filterFn || col.filter) && col.filterVariant) {
        const label = (col.filterLabel ?? col.header ?? '').toString();
        const value = tableState.columnFilters?.find((filter) => filter.id === col.id)
          ?.value as FilterValue;

        filterList.push({
          id: col.id,
          value,
          label,
          data: resolvedData,
          filter: col.filter,
          options: col.filterOptions,
          variant: col.filterVariant,
        });
      }

      // Add subline filter if column has subline and sublineFilterVariant is defined
      if (
        col?.meta?.subline &&
        col.sublineFilterVariant &&
        (col.sublineFilterFn || col.sublineFilter)
      ) {
        const sublineId = `${col.id}-subline`;
        const baseLabel = col.filterLabel ?? col.header;
        const label = col.sublineFilterLabel ?? (baseLabel ? `${baseLabel} (subline)` : 'Subline');
        const value = tableState.columnFilters?.find((filter) => filter.id === sublineId)
          ?.value as FilterValue;

        filterList.push({
          id: sublineId,
          value,
          label,
          data: resolvedData,
          filter: col.sublineFilter,
          options: col.sublineFilterOptions,
          variant: col.sublineFilterVariant,
        });
      }
    }

    return filterList;
  }, [columns, tableState.columnFilters, resolvedData]);

  const filtersMap = useMemo(() => {
    const map = new Map<string, FilterMapVal<RowType>>();
    for (const f of filters) {
      map.set(f.id, f);
    }
    return map;
  }, [filters]);

  const filterOptionsResult = useMemo(() => {
    const optionsMap = new Map<string, { value: string; label: string; group?: string | null }>();
    const groupOrderMap = new Map<string, number>();
    let columnIndex = 0;

    for (const col of columns) {
      let mainColumnAdded = false;

      // Add main column filter option
      if ((col?.filterFn || col?.filter) && col?.filterVariant && col.id) {
        const label = (col.filterLabel ?? col.header) as string;
        const group = col.meta?.group ?? null;
        const groupName = group ?? DEFAULT_FILTER_GROUP_NAME;

        // Track the first occurrence of each group
        if (!groupOrderMap.has(groupName)) {
          groupOrderMap.set(groupName, columnIndex);
        }

        optionsMap.set(col.id, {
          value: col.id,
          label,
          group,
        });
        columnIndex += 1;
        mainColumnAdded = true;
      }

      // Add subline filter option if column has subline and sublineFilterVariant
      if (
        col?.meta?.subline &&
        col.sublineFilterVariant &&
        (col.sublineFilterFn || col.sublineFilter) &&
        col.id
      ) {
        const sublineId = `${col.id}-subline`;
        const baseLabel = col.filterLabel ?? col.header;
        const label = col.sublineFilterLabel ?? (baseLabel ? `${baseLabel} (subline)` : 'Subline');
        const group = col.meta?.group ?? null;
        const groupName = group ?? DEFAULT_FILTER_GROUP_NAME;

        // Track the first occurrence of each group
        // If main column was added, use its index; otherwise use current columnIndex
        if (!groupOrderMap.has(groupName)) {
          groupOrderMap.set(groupName, mainColumnAdded ? columnIndex - 1 : columnIndex);
        }

        optionsMap.set(sublineId, {
          value: sublineId,
          label,
          group,
        });
      }
    }
    return { options: Array.from(optionsMap.values()), groupOrder: groupOrderMap };
  }, [columns]);

  const { options: filterOptions, groupOrder } = filterOptionsResult;

  const handleFiltersChange = useCallback(
    (id: string, value: FilterValue) => {
      let newFilters = [];
      const prevFilter = tableState.columnFilters?.find((col) => col.id === id);
      if (prevFilter) {
        newFilters =
          tableState.columnFilters?.map((col) => {
            if (col.id === id) {
              return {
                ...col,
                value,
              };
            }
            return { ...col };
          }) ?? [];
      } else {
        newFilters = [...(tableState.columnFilters ?? []), { id, value }];
      }
      tableState.setColumnFilters(newFilters);
    },
    [tableState],
  );

  const handleSelectedFiltersChange = useCallback(
    (ids: string[]) => {
      function initValForVariant(variant?: FilterVariant): unknown {
        if (variant) {
          switch (variant) {
            case 'select':
              return null;
            case 'multi-select':
              return [];
            case 'text':
              return '';
            case 'number-range':
            case 'date-range':
              return [null, null];
            case 'number':
              return null;
            case 'boolean':
              return true;
            default:
              return null;
          }
        }
        return null;
      }

      // Find newly added filters
      const currentFilterIds = new Set(tableState.columnFilters?.map((filter) => filter.id) || []);
      const newlyAddedFilters = ids.filter((id) => !currentFilterIds.has(id));

      const newFilters = [];
      for (const id of ids) {
        const prevFilter = tableState.columnFilters?.find((filter) => filter.id === id);
        if (prevFilter) {
          newFilters.push(prevFilter);
        } else {
          const newFilter = filtersMap.get(id);
          if (newFilter) {
            newFilters.push({
              id,
              value: initValForVariant(newFilter?.variant),
            });
          }
        }
      }

      // Automatically open newly added filters
      if (newlyAddedFilters.length > 0) {
        setOpenFilters((prev) => new Set([...prev, ...newlyAddedFilters]));
      }

      tableState.setColumnFilters(newFilters);
    },
    [filtersMap, tableState],
  );

  const handleExportToCSV = useCallback(() => {
    tableState.tableRef?.current?.exportToCSV();
  }, [tableState.tableRef]);

  const handleClearFilters = useCallback(() => {
    tableState.setColumnFilters([]);
  }, [tableState]);

  const handleClearFilter = useCallback(
    (id: string) => {
      const filteredIds =
        tableState.columnFilters?.filter((filter) => filter.id !== id).map((filter) => filter.id) ??
        [];
      handleSelectedFiltersChange(filteredIds);
    },
    [tableState, handleSelectedFiltersChange],
  );

  const handleFilterOpenChange = useCallback((id: string, isOpen: boolean) => {
    setOpenFilters((prev) => {
      const newSet = new Set(prev);
      if (isOpen) {
        newSet.add(id);
      } else {
        newSet.delete(id);
      }
      return newSet;
    });
  }, []);

  const hasColumns = Boolean(columns?.length !== 0);
  const hasFilters = Boolean(hasColumns && tableState.columnFilters?.length > 0);
  const hasControls = Boolean(controls || hasFilters);
  const [ref, { width }] = useMeasure();

  const isNarrow = Boolean(width && width < 700);

  const columnsForTableColumns = useMemo(
    () =>
      disableColumnOrder && columns?.length
        ? columns.map((col) => ({
            ...col,
            meta: {
              ...col.meta,
              fixedPosition: col.meta?.fixedPosition ?? ('as-in' as const),
            },
          }))
        : columns,
    [columns, disableColumnOrder],
  );

  const hasTableCustomization = Boolean(enableCustomizationMenu && columns.length > 0);
  const hasTableSorting = Boolean(enableSortMenu && columns.length > 0);
  const hasStartControls = Boolean(hasTableCustomization || hasTableSorting);
  const hasOverflowActions =
    (overflowActions?.length ?? 0) > 0 || (tableState.tableRef && !disableCSVExport);

  return (
    <TableFiltersContainer ref={ref} isNarrow={isNarrow}>
      <TableFiltersViews relation="separate" size="small">
        {multiSelectActions && multiSelectActions}
        {tableState.presets && <TablePresets presets={tableState.presets} />}
        {hasStartControls && (
          <ControlGroup relation="joined" size="small">
            {hasTableCustomization && (
              <TableColumnsEnhanced<RowType>
                columns={columnsForTableColumns}
                tableState={tableState}
              />
            )}
            {hasTableSorting && <TableSort columns={columns} tableState={tableState} />}
          </ControlGroup>
        )}
        {staticFilters && staticFilters}
        {hasColumns && filters.length > 0 && !isNarrow && (
          <TableFiltersDivider appearance="thick" direction="column" variant="neutral" />
        )}
        {filters.length > 0 && tableState.columnFilters.length === 0 && (
          <SelectedFiltersDropdown
            options={filterOptions}
            selectedFilters={tableState.columnFilters.map((filter) => filter.id)}
            setSelectedFilters={handleSelectedFiltersChange}
            groupOrder={groupOrder}
          />
        )}
      </TableFiltersViews>
      {hasColumns && filters.length > 0 && tableState.columnFilters.length > 0 && (
        <TableFiltersOptions>
          <TableFiltersOptionsScroll
            isNarrow={isNarrow}
            hasViews
            hasControls={hasControls}
            ref={optionsRef}
            onScroll={onScrollHandler}
            {...getBoxShadow}
          >
            {tableState.columnFilters.map((filter) => {
              const mappedFilter = filtersMap.get(filter.id);
              const options = normalizeFilterOptions(
                data ?? tableState.tableRef?.current?.data,
                mappedFilter?.options,
              );

              if (!mappedFilter) return null;

              const sharedTokenProps = {
                id: mappedFilter.id,
                key: mappedFilter.id,
                handle: mappedFilter.label,
                value: <DisplayForFilterValue value={mappedFilter.value} options={options} />,
                onTokenClear: () => handleClearFilter(mappedFilter.id),
                modified: isFilterApplied(mappedFilter.value),
              };

              if (mappedFilter.variant === 'boolean') {
                return (
                  <TableFiltersToken
                    {...sharedTokenProps}
                    variant="toggle"
                    onToggleClick={() =>
                      handleFiltersChange(mappedFilter.id, !(mappedFilter.value as boolean))
                    }
                    toggleSelected={mappedFilter.value as boolean}
                  />
                );
              }

              return (
                <TableFiltersToken
                  {...sharedTokenProps}
                  changeable
                  isOpen={openFilters.has(mappedFilter.id)}
                  onOpenChange={(isOpen) => handleFilterOpenChange(mappedFilter.id, isOpen)}
                >
                  <TableFilter
                    mappedFilter={mappedFilter}
                    options={options}
                    handleFiltersChange={handleFiltersChange}
                  />
                </TableFiltersToken>
              );
            })}
            {tableState.columnFilters.length < filterOptions.length && (
              <SelectedFiltersDropdown
                options={filterOptions}
                selectedFilters={tableState.columnFilters.map((filter) => filter.id)}
                setSelectedFilters={handleSelectedFiltersChange}
                groupOrder={groupOrder}
              />
            )}
            {hasFilters && isNarrow && (
              <Button
                onClick={handleClearFilters}
                appearance="muted"
                arrangement="hidden-label"
                ghost
                icon="trash-can"
                size="small"
                variant="destructive"
              >
                Clear filters
              </Button>
            )}
          </TableFiltersOptionsScroll>
        </TableFiltersOptions>
      )}
      <TableFiltersControls size="small" relation="separate">
        {hasFilters && !isNarrow && (
          <Button
            onClick={handleClearFilters}
            appearance="muted"
            arrangement="hidden-label"
            ghost
            icon="trash-can"
            size="small"
            variant="destructive"
          >
            Clear filters
          </Button>
        )}
        {hasFilters && controls && !isNarrow && (
          <TableFiltersDivider appearance="thick" direction="column" variant="neutral" />
        )}
        {controls && controls}
        {hasOverflowActions && (
          <DropdownMenu>
            <DropdownMenuButton
              appearance="muted"
              arrangement="hidden-label"
              icon="overflow-horizontal"
              variant="secondary"
            >
              More actions
            </DropdownMenuButton>
            <DropdownMenuPopover>
              <TableFiltersActionsMenu actions={overflowActions} />
              {tableState.tableRef && !disableCSVExport && (
                <DropdownMenuGroup>
                  <DropdownMenuItem
                    onClick={handleExportToCSV}
                    icon="export"
                    description={
                      tableState.isServerSidePaginated
                        ? 'Exports the current page of data only.'
                        : undefined
                    }
                    disabled={tableState.numRows === 0}
                  >
                    Export CSV
                  </DropdownMenuItem>
                </DropdownMenuGroup>
              )}
            </DropdownMenuPopover>
          </DropdownMenu>
        )}
        {!hideGlobalFilter && (
          <KeyBoundSearchInput
            searchParamStateKey={`${tableState.storageKey}-filter`}
            shortcutPriority={shortcutPriority}
            aria-label="Filter"
            value={tableState.globalFilter}
            onChange={tableState.setGlobalFilter}
          />
        )}
      </TableFiltersControls>
    </TableFiltersContainer>
  );
}

function ServerTableFiltersEnhanced<RowType, C extends SharedColumnDef<RowType, any>>({
  multiSelectActions,
  columns = [],
  enableCustomizationMenu = true,
  enableSortMenu = false,
  disableColumnOrder = false,
  controls,
  overflowActions,
  table,
  tableState,
}: ServerTableFiltersEnhancedProps<RowType, C>) {
  const [ref, { width }] = useMeasure();
  const optionsRef = useRef<HTMLDivElement>(null);
  const { getBoxShadow, onScrollHandler } = useHorizontalShadowOnScroll('both');
  const isNarrow = width !== null && width < 700;
  const hasTableCustomization = enableCustomizationMenu && columns.length > 0;
  const hasTableSorting = enableSortMenu && columns.length > 0;
  const hasStartControls = hasTableCustomization || hasTableSorting;
  const hasColumns = columns?.length !== 0;
  const hasOverflowActions = (overflowActions?.length ?? 0) > 0;
  const hasFilters = hasColumns && table.filters.length > 0;
  const hasControls = !!controls || table.filters.length > 0;
  const orderedColumns = useMemo(
    () =>
      disableColumnOrder && columns?.length
        ? columns.map((col) => ({
            ...col,
            meta: {
              ...col.meta,
              fixedPosition: col.meta?.fixedPosition ?? ('as-in' as const),
            },
          }))
        : columns,
    [columns, disableColumnOrder],
  );
  const filterOptions: SelectedFiltersDropdownOptions[] = useMemo(() => {
    if (!table.filterDefinitions) {
      return [];
    }
    return table.filterDefinitions.map((definition) => ({
      value: definition.id,
      label: definition.label,
    }));
  }, [table.filterDefinitions]);
  const handleSelectedFilters = useCallback(
    (ids: string[]) => {
      table.setFilters(
        ids
          .map<Filter | undefined>((id) => {
            const filter = table.getFilterByID(id);
            if (filter) {
              return filter;
            }
            const definition = table.getFilterDefinition(id);
            if (!definition) {
              return undefined;
            }
            switch (definition.variant) {
              case TableFilterVariant.Bool:
                return {
                  key: definition.id,
                  type: definition.variant,
                  value: true,
                };
              case TableFilterVariant.Text:
                return {
                  key: definition.id,
                  type: definition.variant,
                  value: '',
                };
              case TableFilterVariant.Number:
                return {
                  key: definition.id,
                  type: definition.variant,
                  value: 0,
                };
              case TableFilterVariant.Select:
                return {
                  key: definition.id,
                  type: definition.variant,
                  value: '',
                };
              case TableFilterVariant.DateRange:
                return {
                  key: definition.id,
                  type: definition.variant,
                  start: null,
                  end: null,
                };
              case TableFilterVariant.NumberRange:
                return {
                  key: definition.id,
                  type: definition.variant,
                  start: null,
                  end: null,
                };
              case TableFilterVariant.MultiSelect:
                return {
                  key: definition.id,
                  type: definition.variant,
                  values: [],
                };
              default:
                return undefined;
            }
          })
          .filter((filter) => !!filter),
      );
    },
    [table],
  );
  const handleClearFilters = useCallback(() => {
    table.setFilters([]);
  }, [table]);

  return (
    <TableFiltersContainer ref={ref} isNarrow={isNarrow}>
      <TableFiltersViews relation="separate" size="small">
        {multiSelectActions && multiSelectActions}
        {hasStartControls && (
          <ControlGroup relation="joined" size="small">
            {hasTableCustomization && (
              <TableColumnsEnhanced<RowType> columns={orderedColumns} tableState={tableState} />
            )}
            {hasTableSorting && <TableSort columns={columns} tableState={tableState} />}
          </ControlGroup>
        )}
        {hasColumns &&
          table.filterDefinitions &&
          table.filterDefinitions.length > 0 &&
          !isNarrow && (
            <TableFiltersDivider appearance="thick" direction="column" variant="neutral" />
          )}
        {table.filterDefinitions &&
          table.filterDefinitions.length > 0 &&
          table.filters.length === 0 && (
            <SelectedFiltersDropdown
              options={filterOptions}
              selectedFilters={table.filters.map((filter) => filter.key)}
              setSelectedFilters={handleSelectedFilters}
              loading={table.filterDefinitions === undefined}
            />
          )}
      </TableFiltersViews>
      {hasColumns &&
        table.filters.length > 0 &&
        table.filterDefinitions &&
        table.filterDefinitions.length > 0 && (
          <TableFiltersOptions>
            <TableFiltersOptionsScroll
              isNarrow={isNarrow}
              hasViews
              hasControls={hasControls}
              ref={optionsRef}
              onScroll={onScrollHandler}
              {...getBoxShadow}
            >
              {table.filters.map((filter) => (
                <ServerTableFiltersToken key={filter.key} table={table} filter={filter} />
              ))}
              {table.filters.length < filterOptions.length && (
                <SelectedFiltersDropdown
                  options={filterOptions}
                  selectedFilters={table.filters.map((filter) => filter.key)}
                  setSelectedFilters={handleSelectedFilters}
                  loading={table.filterDefinitions === undefined}
                />
              )}
              {hasFilters && isNarrow && (
                <Button
                  onClick={handleClearFilters}
                  appearance="muted"
                  arrangement="hidden-label"
                  ghost
                  icon="trash-can"
                  size="small"
                  variant="destructive"
                >
                  Clear filters
                </Button>
              )}
            </TableFiltersOptionsScroll>
          </TableFiltersOptions>
        )}
      <TableFiltersControls size="small" relation="separate">
        {hasFilters && !isNarrow && (
          <Button
            onClick={handleClearFilters}
            appearance="muted"
            arrangement="hidden-label"
            ghost
            icon="trash-can"
            size="small"
            variant="destructive"
          >
            Clear filters
          </Button>
        )}
        {hasFilters && controls && !isNarrow && (
          <TableFiltersDivider appearance="thick" direction="column" variant="neutral" />
        )}
        {controls && controls}
        {hasOverflowActions && (
          <DropdownMenu>
            <DropdownMenuButton
              appearance="muted"
              arrangement="hidden-label"
              icon="overflow-horizontal"
              variant="secondary"
            >
              More actions
            </DropdownMenuButton>
            <DropdownMenuPopover>
              <TableFiltersActionsMenu actions={overflowActions} />
            </DropdownMenuPopover>
          </DropdownMenu>
        )}
      </TableFiltersControls>
    </TableFiltersContainer>
  );
}

// Enhanced component that works with TableState
export function TableFiltersEnhanced<RowType, C extends SharedColumnDef<RowType, any>>(
  props: TableFiltersEnhancedProps<RowType, C>,
) {
  if (!props.serverSide) {
    return <ClientTableFiltersEnhanced {...props} />;
  }
  return <ServerTableFiltersEnhanced {...props} />;
}
